import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid2,
  Divider,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  HelpOutline,
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
} from '@mui/icons-material';

const helpSteps = [
  {
    label: 'Select Schedule',
    description: 'Find your slot in the Horizontal Schedule List. This pre-fills your session metadata.',
    description_1: 'Otherwise manually select session date and start time.'
  },
  {
    label: 'Verify Session',
    description: 'Click the submit icon in the Session Card. This unlocks the student selection list.',
    description_1: ''
  },
  {
    label: 'Add Students',
    description: 'Select students from the list. Feel free to sort, select by groups or manually select each student.',
    description_1: 'Once you have selected all students, click on Add students.'
  },
  {
    label: 'Submit Table',
    description: 'Review the final table, add notes/assessments, time duration subject etc.',
    description_1: 'Click the final Submit button and done!'
  },
];

export default function TutorHelp() {
  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <HelpOutline color="primary" sx={{ fontSize: 32, mr: 1 }} />
        <Typography variant="h4" fontWeight={700}>
          Tutor Help Center
        </Typography>
      </Box>

      {/* --- Workflow Guide --- */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Session Tracking Workflow
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Follow these steps in order to ensure your data is validated and saved correctly.
        </Typography>
        <Stepper  alternativeLabel sx={{ mt: 2 }}>
          {helpSteps.map((step) => (
            <Step key={step.label} active={true}>
              <StepLabel>
                <Typography variant="body2" fontWeight={600}>{step.label}</Typography>
                <Typography variant="caption" display="block">{step.description}</Typography>
                <Typography variant="caption" display="block">{step.description_1}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Grid2 container spacing={4}>
        {/* --- FAQ Section --- */}
        <Grid2 size={{ xs: 12, md: 7 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Common Questions
          </Typography>
          
          <Accordion disableGutters elevation={0} sx={{ borderBottom: '1px solid #eee' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">Why is the student list empty?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                The list filters based on the <strong>Location</strong>, <strong>Program</strong>, and <strong>Semester</strong> selected in the Session Card. If these don't match the students' profile, they won't appear.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0} sx={{ borderBottom: '1px solid #eee' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">How do I handle a Substitute session?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                Toggle the "Substitute" switch in the Session Card. You can then select a Substitute ID. This ensures you get credit for the session while linking it to the correct records.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0} sx={{ borderBottom: '1px solid #eee' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">I made a mistake in the assessments.</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                Assessments are stored in your browser's session storage. If you haven't submitted yet, you can change them in the <strong>Selected Students Table</strong>. If you have already submitted, please contact an administrator.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid2>

        {/* --- Error Codes/Alerts --- */}
        <Grid2 size={{ xs: 12, md: 5 }}>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Troubleshooting Alerts
          </Typography>
          
          <Alert icon={<ErrorOutline fontSize="inherit" />} severity="error" sx={{ mb: 2 }}>
            <strong>"Please validate..."</strong>
            <Typography variant="caption" display="block">
              You tried to submit without clicking "Verify" on the Session Card first.
            </Typography>
          </Alert>

          <Alert icon={<InfoOutlined fontSize="inherit" />} severity="info">
            <strong>Connection Errors</strong>
            <Typography variant="caption" display="block">
              If the loading dialog spins indefinitely, check your internet connection. Your progress is temporarily saved in the browser.
            </Typography>
          </Alert>
        </Grid2>
      </Grid2>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Need technical support? Contact the administrator.
        </Typography>
      </Box>
    </Box>
  );
}